import Phaser from 'phaser'
import Chance from 'chance'
import Config from '../launch/config'
import {_addToEventLog, forceAppUpdate} from '../app/app'
import Effects from '../effects/effects'
import {isDebug} from '../launch/utils'
import {orbitTowable} from './utils'
import audio from '../launch/audio'

export default class Entity {
    constructor(props) {
        const {
            diameter,
            discovered,
            faction,
            fillColor,
            id,
            isEnemyHomePlanet,
            isHomePlanet,
            isMiningResources,
            name,
            orbitingShips,
            orbitingTowables,
            type,
            x,
            y,
        } = props
        const chance = new Chance()

        this.type = type
        this.id = id || chance.guid()
        this.name =
            name ||
            chance.capitalize(
                chance.pick([
                    chance.word({syllables: 1}),
                    chance.word({syllables: 2}),
                    chance.word({syllables: 3}),
                ])
            )
        this.faction = faction || null
        this.isEnemyHomePlanet = isEnemyHomePlanet || false
        this.isHomePlanet = isHomePlanet || false
        this.isMiningResources = isMiningResources || false
        this.discovered = discovered || false
        this.selected = false
        this.orbitingShips = orbitingShips || {}
        this.orbitingTowables = orbitingTowables || {}
        this.diameter = diameter
        this.sensorRange =
            props.diameter +
            Config.difficultyLevels[Config.difficulty].planetSensorRange
        this.x =
            x ||
            Phaser.Math.Between(
                Config.boardPadding,
                Config.boardSizes[Config.boardSize].boardWidth -
                    Config.boardPadding
            )
        this.y =
            y ||
            Phaser.Math.Between(
                Config.boardPadding,
                Config.boardSizes[Config.boardSize].boardHeight -
                    Config.boardPadding
            )

        this.fillColor =
            fillColor || new Phaser.Display.Color().random(10).color

        this.circle = new Phaser.Geom.Circle(this.x, this.y, this.diameter)

        // Graphics
        if (!this.discovered) {
            this.undiscoveredImage = Config.scene.add.image(
                props.x,
                props.y,
                'undiscovered'
            )
            this.undiscoveredImage.setScale(0.25)
            this.undiscoveredImage.setDepth(2)
            this.undiscoveredImage[Config.namespace] = {
                type: 'entity undiscovered',
            }
        }

        // Matter body
        this.body = Config.scene.matter.add.circle(
            this.x,
            this.y,
            this.diameter,
            {
                isStatic: true,
                isSensor: true,
            }
        )
        this.body[Config.namespace] = {
            isEntityBody: true,
            entityId: this.id,
        }

        this.orbitBody = Config.scene.matter.add.circle(
            this.x,
            this.y,
            Config.orbitDistance,
            {
                isStatic: true,
                isSensor: true,
            }
        )
        this.orbitBody[Config.namespace] = {
            isOrbitBody: true,
            entityId: this.id,
        }

        // this.sensorBody = Config.scene.matter.add.circle(
        //     this.x,
        //     this.y,
        //     this.sensorRange,
        //     {
        //         isStatic: true,
        //         isSensor: true,
        //     }
        // )
        // this.sensorBody[Config.namespace] = {
        //     isEntitySensorBody: true,
        //     entityId: this.id,
        // }

        this.sensorCircle = new Phaser.Geom.Circle(
            this.x,
            this.y,
            this.sensorRange
        )
        this.sensorCircle[Config.namespace] = {type: 'sensorCircle'}

        // Draw sensor circle
        // this.sensorGraphics = Config.scene.add.graphics({
        //     lineStyle: {width: 1, color: 0xffffff, alpha: 1},
        // })
        // this.sensorGraphics.strokeCircleShape(this.sensorCircle)
        // this.sensorGraphics[Config.namespace] = {type: 'sensorGraphics'}

        // Label
        this.labelWidth = Config.orbitDistance * 2 - 30
        this.label = Config.scene.add.text(
            this.x - this.labelWidth / 2,
            this.y + this.diameter + 2,
            this.name,
            {
                fontFamily: 'sans-serif',
                fontSize: '10px',
                color: '#fff',
                align: 'center',
                fixedWidth: this.labelWidth,
            }
        )
        this.label.depth = 1
        this.label[Config.namespace] = {type: 'entity label'}
        this.label.setResolution(3)

        // Status Label
        this.statusLabel = Config.scene.add.text(
            this.x - this.labelWidth / 2,
            this.y + this.diameter + 13,
            '',
            {
                fontFamily: 'sans-serif',
                fontSize: '10px',
                color: `#${Config.colorGreen}`,
                align: 'center',
                fixedWidth: this.labelWidth,
            }
        )
        this.statusLabel.depth = 1
        this.statusLabel[Config.namespace] = {type: 'entity statusLabel'}
        this.statusLabel.setResolution(3)

        this.labelsHidden = false

        // Hide labels when zoomed out as they become pixelated and unreadable
        Config.scene.cameras.main.on(
            Phaser.Cameras.Scene2D.Events.ZOOM_COMPLETE,
            (camera, zoom) => {
                if (zoom.destination < 1) {
                    this.labelsHidden = true
                } else {
                    this.labelsHidden = false
                }
            }
        )

        // Draw orbit path
        this.orbitGraphics?.clear()
        this.orbitGraphics = Config.scene.add.graphics({
            lineStyle: {width: 2, color: 0xffffff, alpha: 0.2},
        })
        this.orbitRing = new Phaser.Geom.Circle(
            this.x,
            this.y,
            Config.orbitDistance
        )
        this.orbitGraphics.setDepth(1)
        this.orbitGraphics.strokeCircleShape(this.orbitRing)
        this.orbitGraphics[Config.namespace] = {type: 'orbitGraphics'}

        // Register Entity
        Config.entities[this.id] = this

        Object.keys(this.orbitingTowables).forEach((key) => {
            orbitTowable(this, key)
        })
    }

    destroy() {
        this.undiscoveredImage?.destroy()
        this.label.destroy()
        this.statusLabel.destroy()
        this.sensorGraphics?.destroy()
        this.orbitGraphics?.destroy()
        Config.scene.matter.world.remove(this.body)
        Config.scene.matter.world.remove(this.orbitBody)
        Config.scene.matter.world.remove(this.orbitRing)
        Config.scene.matter.world.remove(this.circle)
        Config.scene.matter.world.remove(this.sensorCircle)
        delete Config.entities[this.id]
        delete this
    }

    handlePointerdown(x, y) {
        if (
            Phaser.Geom.Intersects.CircleToCircle(this.circle, {
                x,
                y,
                radius: Config.planetMaxSize,
            })
        ) {
            if (Object.keys(Config.selectedShips).length) {
                return
            }
            this.selected = this.selected ? false : true
            audio.entitySelect()
            isDebug && console.log(this)

            // Center on planet
            // this.type === 'planet' &&
            //     Config.scene.cameras.main.pan(x, y, 300, 'Linear', true)
        } else {
            this.selected = false
        }
        forceAppUpdate()
    }

    deselect() {
        this.selected = false
    }

    update() {
        // const hasOrbitingShips = Object.keys(this.orbitingShips).length
        this.hasOrbitingTowables = Object.keys(this.orbitingTowables).length
        this.hasDetectedOrbitingShips = false
        this.hasFriendlyOrbitingShips = false
        this.hasEnemyOrbitingShips = false

        Object.keys(this.orbitingShips).forEach((key) => {
            const ship = Config.ships[key]

            if (ship.faction === Config.userFactionName) {
                this.hasFriendlyOrbitingShips = true

                if (!this.discovered) {
                    const difficulty =
                        Config.difficultyLevels[Config.difficulty]

                    Config.gameStats.score += difficulty.planetDiscoverPoints

                    new Effects().scoreAnim({
                        x: this.x,
                        y: this.y,
                        num: difficulty.planetDiscoverPoints,
                        positive: true,
                    })
                    forceAppUpdate()
                    _addToEventLog({
                        html: `The ${this.type} <span style="color: #${
                            this.faction === Config.enemyFactionName
                                ? Config.colorRed
                                : 'fff'
                        }">${this.name}</span> has been discovered`,
                        coords: {x: this.x, y: this.y},
                    })
                }
            }

            if (ship.faction.includes(Config.enemyFactionName)) {
                this.hasEnemyOrbitingShips = true
            }

            if (ship.isDetected) {
                this.hasDetectedOrbitingShips = true
            }
        })

        this.label.setVisible(
            (!this.labelsHidden && this.discovered) || isDebug
        )
        this.statusLabel.setVisible(
            (!this.labelsHidden && this.discovered) || isDebug
        )

        if (
            (this.colonized && this.faction === Config.userFactionName) ||
            this.hasFriendlyOrbitingShips ||
            Config.isGameOver
        ) {
            this.discovered = true

            if (this.undiscoveredImage) {
                this.undiscoveredImage.destroy()
                delete this.undiscoveredImage
            }
        }

        if (this.undiscoveredImage) {
            this.undiscoveredImage.alpha = this.selected ? 1 : 0.7
        }

        this.orbitGraphics.setVisible(
            this.hasFriendlyOrbitingShips ||
                this.hasOrbitingTowables ||
                (this.hasEnemyOrbitingShips && this.hasDetectedOrbitingShips) ||
                (this.hasEnemyOrbitingShips && Config.isGameOver) ||
                (this.hasEnemyOrbitingShips && Config.isDemo)
        )
    }
}
